import React from 'react'
import { 
  FiX, 
  FiTag, 
  FiUsers, 
  FiClock, 
  FiDollarSign, 
  FiSave,
  FiLoader,
  FiAlertCircle,
  FiEdit2,
  FiUser,
  FiCalendar,
  FiTrendingUp,
  FiAward
} from "react-icons/fi";
import { 
  MdOutlineEdit, 
  MdOutlinePerson,
  MdOutlinePeople,
  MdOutlineAccessTime,
  MdOutlineAttachMoney,
  MdOutlineClose,
  MdOutlineSave,
  MdOutlineLocalOffer,
  MdOutlineCampaign
} from "react-icons/md";
import { IoPricetagOutline, IoPeopleOutline, IoTimeOutline, IoCashOutline } from "react-icons/io5";
import InputField from '../../../components/ui/Input'

const EditAdsModal = ({ 
  onCancel, 
  onSubmit, 
  title, 
  editAdsData, 
  handleONChange, 
  loading = false 
}) => {
  // Check if form is valid
  const isFormValid = editAdsData?.name?.trim() && editAdsData?.audience?.trim() && editAdsData?.period && editAdsData?.charges;

  // Format the title based on whether we're editing or creating
  const modalTitle = title || (editAdsData?._id ? "Edit Advertisement" : "Create Advertisement");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <MdOutlineEdit className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{modalTitle}</h3>
              <p className="text-xs text-gray-400 hidden sm:block">
                {editAdsData?._id ? 'Update advertisement details' : 'Create a new advertisement'}
              </p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiTag className="w-4 h-4 text-gray-400" />
              <span>Name</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoPricetagOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="name"
                value={editAdsData?.name || ""}
                onChange={handleONChange}
                placeholder="Enter advertisement name..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Audience */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiUsers className="w-4 h-4 text-gray-400" />
              <span>Audience</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoPeopleOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="audience"
                value={editAdsData?.audience || ""}
                onChange={handleONChange}
                placeholder="Enter target audience..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiClock className="w-4 h-4 text-gray-400" />
              <span>Period (Days)</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoTimeOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="period"
                value={editAdsData?.period || ""}
                onChange={handleONChange}
                placeholder="Enter duration in days..."
                min="1"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Charges */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiDollarSign className="w-4 h-4 text-gray-400" />
              <span>Charges</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoCashOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="charges"
                value={editAdsData?.charges || ""}
                onChange={handleONChange}
                placeholder="Enter charges..."
                min="0"
                step="0.01"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Form Validity Indicator */}
          {!isFormValid && (
            <div className="flex items-center gap-3 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in duration-200">
              <FiAlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p>Please fill in all required fields to update the advertisement</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid && !loading
                ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5'
                : 'bg-gray-300 cursor-not-allowed opacity-70'
            }`}
            onClick={onSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4" />
                <span>{editAdsData?._id ? "Update Ads" : "Create Ads"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditAdsModal